import { DataService } from '../../../services/data_service';
import { fn2nZ, toFixedDecimal } from '../../../core/formatter/formatNumber';
import { getBasisjahr, getParameter, getPreisVars, transformValue } from './Energiekosten';
import { getEnergietraegerKeyMap } from '../../../main/functions/getEnergietraegerKeyMap';
import { IDs } from '../../../helpers/IDs';
import { lazyInject } from '../../../services/inversify';
import optionen from './energiekosten_optionen.json';
import * as React from 'react';
import { renderTable } from './renderTable';
import { WHelpIcon } from '../../../core/elements/WHelpIcon';

export interface EnergiekostenPrognoseProps {
    custom?: any;
    data: any;
    disabled?: boolean;
    energietraeger: any;
    getData?: (data: any) => void;
    inflation: any;
    onOptionChange?: (option: string) => void;
    option: string;
    optionen: any;
}

export interface EnergiekostenPrognoseState {
    energietraeger: any;
    jahre: number[];
}

export class EnergiekostenPrognose extends React.Component<EnergiekostenPrognoseProps, EnergiekostenPrognoseState> {

    constructor(props: any) {
        super(props);
        this.state = {
            energietraeger: null,
            jahre: [],
        };
        this.onFocus = this.onFocus.bind(this);
    }

    @lazyInject(DataService) private dataService: DataService;

    private curInput: any;
    private interval: number = 5;
    private jahr: number = new Date().getFullYear();
    private optionen: any = { ...(optionen || {}) };
    private saveTimeoutId: number;
    private sumKosten: any = {};
    private values: any = {};

    componentDidMount(): void {
        this.init();
    }

    componentDidUpdate(prevProps: any): void {
        if (this.props.option !== prevProps.option) {
            if (this.props.option !== 'custom') {
                this.curInput = null;
            }
            this.init();
            this.saveParameter(0, this.props.option === 'custom');
        } else if (JSON.stringify(this.props.energietraeger) !== JSON.stringify(prevProps.energietraeger)) {
            this.saveParameter(0, this.props.option !== 'custom');
            this.setSelection();
        } else if (JSON.stringify(this.props.inflation) !== JSON.stringify(prevProps.inflation)) {
            this.saveParameter(0, this.props.option !== 'custom');
        }
    }

    componentWillUnmount(): void {
        if (this.saveTimeoutId) {
            this.saveParameter(0);
        }
    }

    getData(key: string, callback: (data: any) => void): any[] {
        const data: any = this.state.energietraeger[key] || {};
        const basisjahr: number = data.jahr;
        const energietraeger: any = (getEnergietraegerKeyMap() || {})[key] || {};
        const inflation: any = this.props.inflation || {};
        const items: any[] = [];
        const kennzahlen: any = (this.props.data || {})[IDs.KENNZIFFERN_BERECHNET] || {};
        const steigerung: number = data.steigerung;
        let verbrauch: number = (kennzahlen[basisjahr] || {})[energietraeger.vKey];

        if (typeof verbrauch === 'undefined') {
            const basisjahrFallback: number = getBasisjahr(energietraeger, this.props.data);
            verbrauch = (kennzahlen[basisjahrFallback] || {})[energietraeger.vKey];
        }
        const { startJahr } = getPreisVars(data.preise || {});
        let valueInflation: number | string = typeof inflation.value === 'string' ? inflation.value.trim() : inflation.value;
        valueInflation = valueInflation !== '' && !isNaN(+valueInflation)
            ? +valueInflation
            : (valueInflation === '0' ? 0 : null);
        const faktorInflation: number = inflation.checked ? valueInflation : 0;
        items.push(energietraeger.name2 || energietraeger.name);
        items.push(null);

        this.state.jahre.forEach((jahr: number) => {
            const preis: number = (data.preise || {})[jahr];
            const kosten: number = preis * verbrauch * (1 + (+faktorInflation / 100) * (jahr - startJahr));
            items.push(this.renderPreis(preis, key, jahr));
            items.push(fn2nZ(kosten));

            if (typeof callback === 'function') {
                callback({ jahr, kosten, preis });
            }
        });
        items[1] = this.renderSteigerung(steigerung, key);

        return items;
    }

    getDataRows(): any {
        const energietraeger: any = getEnergietraegerKeyMap() || {};
        const sumKosten: any = {};
        const tbody: any = { rows: [], rowStyles: [] };
        const values: any = {};
        let odd: boolean = false;
        let rowIndex: number = 0;

        Object.keys(this.state.energietraeger || {}).forEach((key: string) => {
            if (!this.state.energietraeger[key].checked) {
                return;
            }

            if (!getBasisjahr(energietraeger[key] || {}, this.props.data)) {
                return;
            }
            values[key] = {
                name: (energietraeger[key] || {}).name2 || (energietraeger[key] || {}).name,
                jahre: {},
            };
            const row: any[] = this.getData(
                key,
                (item: any) => {
                    const jahr: number = item.jahr || null;

                    if (typeof sumKosten[jahr] === 'undefined') {
                        sumKosten[jahr] = 0;
                    }
                    sumKosten[jahr] += transformValue(item.kosten, 2);
                    values[key].jahre[jahr] = { preis: item.preis, kosten: item.kosten };
                }
            ) || [];

            if (row.length) {
                tbody.rows.push(row);
                tbody.rowStyles[rowIndex] = [];

                row.forEach((cell: any, cellIndex: number) => {
                    if (cellIndex === 0) {
                        tbody.rowStyles[rowIndex].push([['width:210', 'light', 'center']]);
                    } else if (cellIndex === 1) {
                        tbody.rowStyles[rowIndex].push([['width:75', 'lighter', 'center']]);
                    } else {
                        tbody.rowStyles[rowIndex].push([['width:91.5', 'center', ...(odd ? ['light'] : ['lighter'])]]);
                        odd = !odd;
                    }
                });
                ++rowIndex;
            }
        });
        this.sumKosten = sumKosten;

        if (typeof this.props.getData === 'function' && JSON.stringify(values) !== JSON.stringify(this.values)) {
            this.values = values;
            this.props.getData(values);
        }

        return tbody;
    }

    getEnergietraeger(): any {
        if (this.props.option === 'custom') {
            return this.state.energietraeger
                ? (getParameter(this.props.data) || {}).energietraeger || {}
                : this.state.energietraeger || (getParameter(this.props.data) || {}).energietraeger || {};
        }

        return this.optionen[this.props.option] || {};
    }

    getJahreRow(): any {
        const rows: any[] = [
            'Energieträger',
            'jährlich',
        ];
        const rowStyles: any[] = [
            [['width:210', 'head', 'noborder-top', 'noborder-bottom', 'center', 'bold']],
            [['width:75', 'head', 'noborder-top', 'noborder-bottom', 'top', 'center']],
        ];

        (this.state.jahre || []).forEach((jahr: number) => {
            rows.push(jahr);
            rowStyles.push([['width:183', 'head', 'center', 'bold']]);
        });

        return { rows, rowStyles };
    }

    getSteigerung(data: any): number {
        data = data || {};
        const { startPreis, endPreis, diffJahre } = getPreisVars(data || {});

        return startPreis > 0 && diffJahre
            ? ((endPreis - startPreis) / startPreis / diffJahre) : null;
    }

    getTable(): any {
        const tbody: any = this.getTableBody();
        const tfoot: any = this.getTableFooter();
        const thead: any = this.getTableHeader();

        return {
            rows: [...thead.rows, ...tbody.rows, ...tfoot.rows],
            rowStyles: [...thead.rowStyles, ...tbody.rowStyles, ...tfoot.rowStyles],
        };
    }

    getTableBody(): any {
        return this.getDataRows();
    }

    getTableFooter(): any {
        const tfoot: any = { rows: [], rowStyles: [] };
        tfoot.rows.push([
            'Gesamt',
            null,
        ]);
        tfoot.rowStyles.push([
            [['width:210', 'dark', 'bold', 'center']],
            ['width:75'],
        ]);

        this.state.jahre.forEach((jahr: number) => {
            const kosten: string = fn2nZ(this.sumKosten[jahr]);
            tfoot.rows[0].push(null);
            tfoot.rows[0].push(kosten);
            tfoot.rowStyles[0].push(['width:91.5', 'center']);
            tfoot.rowStyles[0].push([['width:91.5', 'center', 'dark', 'bold']]);
        });

        return tfoot;
    }

    getTableHeader(): any {
        const rowJahre: any = this.getJahreRow();
        const option: string = (this.props.optionen || {})[this.props.option] || '';
        const thead: any = { rows: [], rowStyles: [] };
        // #0
        thead.rows.push([
            <div style={{ paddingRight: 30, display: 'inline-flex', fontWeight: 600 }}>
                <span style={{fontWeight: 600}}>Prognose: </span>{`Szenario "${option}"`}{this.renderHelp()}
            </div>,
        ]);
        thead.rowStyles.push([
            [['width:1199', 'title'], { paddingLeft: 10, borderTopLeftRadius: '0pt', borderTopRightRadius: '0pt' }],
        ]);
        // #1
        thead.rows.push([
            null,
            'Steigerung',
            'Energiepreise',
        ]);
        thead.rowStyles.push([
            [['width:210', 'head', 'noborder-bottom', 'bold']],
            [['width:75', 'head', 'noborder-bottom', 'center', 'bold']],
            [['width:915', 'head', 'center', 'bold']],
        ]);
        // #2
        thead.rows = [...thead.rows, rowJahre.rows];
        thead.rowStyles = [...thead.rowStyles, rowJahre.rowStyles];
        // #3
        thead.rows.push([
            null,
            '%',
        ]);
        thead.rowStyles.push([
            [['width:210', 'head', 'noborder-top', 'top', 'bold']],
            [['width:75', 'head', 'noborder-top', 'top', 'center', 'bold']],
        ]);

        this.state.jahre.forEach(() => {
            thead.rows[3].push('€/kWh');
            thead.rows[3].push('Gesamt [€]');
            thead.rowStyles[3].push([['width:91.5', 'head', 'center', 'bold']]);
            thead.rowStyles[3].push([['width:91.5', 'head', 'center', 'bold']]);
        });

        return thead;
    }

    init(): void {
        const energietraeger: any = {};
        const energietraegerDb: any = this.getEnergietraeger();
        const hasData: boolean = !!Object.keys(energietraegerDb).length;
        const jahre: number[] = [];
        const preiseFallback: any = Object.values(this.optionen)[0] || {};

        Object.keys(this.props.energietraeger || {}).forEach((key: string) =>
            energietraeger[key] = { ...this.props.energietraeger[key] }
        );

        for (let i: number = 0; i <= 4; ++i) {
            const jahr: number = this.jahr + (i * this.interval) - ((this.jahr % this.interval) * +!!i);
            jahre.push(jahr);

            Object.keys(energietraeger).forEach((key: string) => {
                const preis: number = hasData
                    ? ((energietraegerDb[key] || {}).preise || {})[jahr]
                    : (((this.state.energietraeger || {})[key] || {}).preise || {})[jahr];
                const preisFallback: number = ((preiseFallback[key] || {}).preise || {})[jahr];

                if (!energietraeger[key].preise) {
                    energietraeger[key].preise = {};
                }
                // energietraeger[key].preise[jahr] = typeof preis !== 'undefined' ? preis : null;
                energietraeger[key].preise[jahr] = typeof preis !== 'undefined'
                    ? preis
                    : (this.props.option === 'custom' && typeof preis === 'undefined' && typeof preisFallback !== 'undefined'
                        ? preisFallback : null
                    );

                if (i === 4) {
                    const steigerung: number = this.getSteigerung(energietraeger[key].preise);
                    energietraeger[key].steigerung = steigerung;
                }
            });
        }
        // console.info('INIT', this.props.option, this.props.inflation, jahre, energietraeger, this.props.data[IDs.ENT_ENERGIEKOSTEN_PROJEKTION]);
        this.setState({ energietraeger, jahre });
    }

    onChangePreis(event: any, key: string, jahr: number): void {
        const { energietraeger } = this.state;
        const curValue: string = event.target.value;
        const value: string = typeof curValue === 'string' ? curValue.trim() : '';
        const preis: number = value !== '' && !isNaN(+value)
            ? +value
            : (value === '0' ? 0 : null);
        energietraeger[key].preise[jahr] = preis;
        const steigerung: number = this.getSteigerung(energietraeger[key].preise);
        energietraeger[key].steigerung = steigerung;
        this.setState({ energietraeger }, () => this.saveParameter(undefined, null));
    }

    onChangeSteigerung(event: any, key: string): void {
        const { energietraeger, jahre } = this.state;
        const { startJahr, startPreis } = getPreisVars(energietraeger[key].preise || {});
        const curValue: string = event.target.value;
        const preise: any = energietraeger[key].preise || {};
        const value: string = typeof curValue === 'string' ? curValue.trim() : '';
        const steigerung: number = value !== '' && !isNaN(+value)
            ? +value
            : (value === '0' ? 0 : null);
        energietraeger[key].steigerung = typeof steigerung === 'number' ? steigerung / 100 : steigerung;

        jahre.forEach((jahr: number) => {
            if (jahr > startJahr) {
                preise[jahr] = startPreis * (1 + (+curValue / 100) * (jahr - startJahr));
                // console.info('CALC linear', {jahr}, preise[jahr], '::', `${startPreis} * (1 + ${+curValue / 100}) * (${jahr} - ${startJahr}))`);
                // const calcExp: number = startPreis * ((1 + (+curValue / 100)) ** (jahr - startJahr));
                // console.info('CALC exponentiell', {jahr}, calcExp, '::', `${startPreis} * ((1 + ${+curValue / 100})) ** (${jahr} - ${startJahr}))`);
            }
        });
        this.setState({ energietraeger }, () => this.saveParameter(undefined, null));
    }

    onFocus(event: any): void {
        this.curInput = event.target;

        if (this.props.option !== 'custom') {
            this.props.onOptionChange('custom');
        }
    }

    render(): JSX.Element {
        if (!this.state.jahre) {
            return null;
        }

        return (
            <React.Fragment key={`prognose-${this.props.option}`}>
                {renderTable(this.getTable())}
            </React.Fragment>
        );
    }

    renderHelp(): JSX.Element {
        return (
            <WHelpIcon
                data={this.props.data}
                styleDivProps={{ marginLeft: 5, display: 'flex', alignSelf: 'center' }}
                styleProps={{ margin: 0 }}
                label='Energiekosten-Projektion - Prognose'
                helpKey='energiekosten_projektion_prognose'
                type='theme_info'
                style='theme_small'
                customElement='Info'
                editBar
                useTheme
            />
        );
    }

    renderPreis(value: number, key: string, jahr: number): JSX.Element {
        const backgroundColor: string = this.props.option === 'custom' ? 'white' : 'inherit';
        const disabled: boolean = this.props.disabled;

        return (
            <input
                key={`input-preis-${key}-${jahr}`}
                id={`input-preis-${key}-${jahr}`}
                style={{ textAlign: 'center', backgroundColor, width: '100%', border: 0, boxSizing: 'border-box' }}
                type='number'
                value={value !== null && !isNaN(+value) ? toFixedDecimal(value, 2) : ''}
                disabled={disabled}
                onChange={(event: any) => this.onChangePreis(event, key, jahr)}
                onFocus={this.onFocus}
            />
        );
    }

    renderSteigerung(value: number, key: string): JSX.Element {
        const backgroundColor: string = this.props.option === 'custom' ? 'white' : 'inherit';
        const disabled: boolean = this.props.disabled;

        return (
            <input
                key={`input-steigerung-${key}`}
                id={`input-steigerung-${key}`}
                style={{ textAlign: 'center', backgroundColor, width: '100%', border: 0, boxSizing: 'border-box' }}
                type='number'
                value={value !== null && !isNaN(+value) ? toFixedDecimal(value * 100, 2) : ''}
                disabled={disabled}
                onChange={(event: any) => this.onChangeSteigerung(event, key)}
                onFocus={this.onFocus}
            />
        );
    }

    saveParameter(delay: number = 2000, noOverwrite?: boolean): void {
        const { energietraeger } = this.state;
        const energietraegerDb: any = (getParameter(this.props.data) || {}).energietraeger || {};
        const energiekostenProjektion: any = ((this.props.data || {})[IDs.ENT_ENERGIEKOSTEN_PROJEKTION] || [])[0] || {};
        const jsonParameterDb: string = energiekostenProjektion.parameter || '{}';
        const tid: number = energiekostenProjektion.tid || 0;
        const parameter: any = {
            option: {
                id: delay ? 'custom' : this.props.option,
            },
            inflation: this.props.inflation,
            energietraeger: noOverwrite && Object.keys(energietraegerDb).length ? energietraegerDb : energietraeger,
        };

        Object.keys(this.props.energietraeger || {}).forEach((key: string) => {
            if (typeof (energietraeger[key] || {}).checked !== 'undefined') {
                parameter.energietraeger[key].checked = this.props.energietraeger[key].checked;
            }
        });
        const jsonParameter: string = JSON.stringify(parameter);

        if (jsonParameter === jsonParameterDb) {
            return;
        }
        const saveTimeoutId: number = window.setTimeout(() => {
            const data: any = { tid, parameter: JSON.stringify(parameter) };
            console.info('saveParameter', tid/*, JSON.stringify(parameter), data*/);
            this.dataService.setData(
                IDs.MOD_ETOOL, IDs.ENT_ENERGIEKOSTEN_PROJEKTION,
                { tid, parameter: JSON.stringify(parameter) },
                (insertID: number) => {
                    if (typeof insertID === 'number') {
                        data.tid = insertID;
                    }
                    this.props.data[IDs.ENT_ENERGIEKOSTEN_PROJEKTION][0] = data;
                    console.info('insertID', {insertID}, {tid}, this.props.option);
                }
            );
            this.saveTimeoutId = null;
        }, delay);
        clearTimeout(this.saveTimeoutId);
        this.saveTimeoutId = saveTimeoutId;
    }

    /*setInputFocus(): void {
        const id: string = (this.curInput || {}).id;
        const element: HTMLInputElement = document.getElementById(id) as HTMLInputElement;

        if (!element) {
            return;
        }
        element.focus();
        const value: string = element.value;
        element.value = '';
        element.value = value;
    }*/

    setSelection(): void {
        const { energietraeger } = this.state;

        Object.keys(this.props.energietraeger || {}).forEach((key: string) => {
            energietraeger[key] = { ...energietraeger[key], ...this.props.energietraeger[key] };
        });
        this.setState({ energietraeger });
    }
}
